import { useEffect, useState } from "react";
import { FileText, Trash2, RefreshCw, Search, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * DocQACard — Settings card to manage the document-Q&A index used by Hidro AI.
 * Lists ingested docs, lets manager+ delete entries, and offers a quick search
 * box to verify chunks return for a given query (handy for sanity-checking).
 */
export default function DocQACard() {
  const { user } = useAuth();
  const allowed = ["manager", "admin", "owner"].includes(user?.role);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/doc-qa/docs");
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load doc list"));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return null;

  const remove = async (gid) => {
    if (!window.confirm("Remove this document's chunks from the Hidro Q&A index?")) return;
    try {
      await api.delete(`/doc-qa/docs/${gid}`);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Delete failed"));
    }
  };

  const runSearch = async () => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearching(true);
    try {
      const { data } = await api.post("/doc-qa/search", { query: trimmed, top_k: 5 });
      setHits(data?.results || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Search failed"));
      setHits([]);
    } finally { setSearching(false); }
  };

  return (
    <div className="hv-card p-5" data-testid="doc-qa-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#1FB6C1]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-bold">Hidro Q&A index</h2>
            <p className="text-xs text-[#a0aabf]">PDFs and text files ingested into Hidro's document search. Used by the <span className="font-mono text-[#1FB6C1]">search_docs</span> AI tool.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="doc-qa-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Reload
        </button>
      </div>

      {err && <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded mb-3">{err}</div>}

      {/* Quick test search */}
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4 text-[#647087]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          placeholder="Test search — e.g. 'call-out fee'"
          className="hv-input text-xs flex-1"
          data-testid="doc-qa-search-input"
        />
        <button
          type="button"
          onClick={runSearch}
          disabled={searching || !q.trim()}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="doc-qa-search-btn"
        >
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Search"}
        </button>
      </div>

      {hits !== null && (
        <div className="mb-4 space-y-1.5" data-testid="doc-qa-search-results">
          {hits.length === 0 ? (
            <div className="text-xs text-[#647087] italic">No matches for that query.</div>
          ) : hits.map((h, i) => (
            <div key={i} className="border border-[#1FB6C1]/20 bg-[#1FB6C1]/5 rounded p-2.5 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-1">
                {h.file_name} · score {h.score?.toFixed?.(2) || h.score}
              </div>
              <div className="text-[#cbd5e1] whitespace-pre-wrap line-clamp-3">{h.excerpt || h.text}</div>
            </div>
          ))}
        </div>
      )}

      {docs.length === 0 ? (
        <div className="text-xs text-[#647087] italic text-center py-4" data-testid="doc-qa-empty">
          No documents ingested yet. From a customer's gallery, click "Add to Hidro Q&A" on any uploaded PDF or text file.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="doc-qa-doc-list">
          {docs.map((d) => (
            <div
              key={d.gallery_item_id}
              className="flex items-center gap-2 border border-white/10 rounded p-2 text-xs"
              data-testid={`doc-qa-row-${d.gallery_item_id}`}
            >
              <FileText className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{d.file_name || "(unnamed)"}</div>
                <div className="text-[10px] text-[#647087]">
                  {d.chunks} chunks · ingested {d.ingested_at ? new Date(d.ingested_at).toLocaleString("en-GB") : "?"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(d.gallery_item_id)}
                className="hv-btn hv-btn-ghost text-xs shrink-0"
                title="Purge from index"
                data-testid={`doc-qa-delete-${d.gallery_item_id}`}
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-300" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
